/**
 * Native wire contract: the passage text size scale, and the one formula that
 * turns it into type metrics.
 *
 * Directory rules (the src/web to src/ios type firewall, FOUND-05): plain types
 * and constants only, zero package imports. This file exports two pure functions
 * as well, on the same footing as NATIVE_ROUTES in the barrel: they import
 * nothing, and the native app value-imports them through Metro watchFolders.
 * They are here rather than in @lib precisely so web and iOS cannot drift - the
 * old enum kept its px table in THREE places (a Tailwind class Record on web, a
 * PREVIEW_TYPE map in native onboarding, and a hardcoded style in the native
 * passage card) and all three had already disagreed.
 *
 * WHY A PERCENT AND NOT A WIDER ENUM. A slider needs ~20 stops. As enum members
 * those would need a label per stop in all three i18n catalogs, for a control
 * that shows no per-stop labels. A percent also lets each platform multiply its
 * own base instead of shipping a px table per platform.
 *
 * WHY MIN/MAX/STEP AND NOT AN OPTIONS ARRAY. The audio speeds ship as an options
 * array and are rendered natively as a Picker, deliberately: see the header of
 * src/ios/src/screens/settings/audio.tsx, which argues that a continuous Slider
 * over a clamped-to-a-list value would let a learner aim at a value the server
 * would refuse or silently round. That objection is answered here rather than
 * ignored - the server's patch schema snaps through the very same
 * clampPassageFontPct() the slider's min/max/step describe, so every value the
 * control can reach is a value the server stores unchanged.
 */

/** Smallest stop: 35% of base = 7.7px. */
export const PASSAGE_FONT_PCT_MIN = 35;
/** Largest stop: 130% of base = 28.6px. */
export const PASSAGE_FONT_PCT_MAX = 130;
/** 20 stops from min to max, each 1.1px apart. */
export const PASSAGE_FONT_PCT_STEP = 5;
/** 100% is 22px, the size every passage rendered before the slider existed. */
export const PASSAGE_FONT_BASE_PX = 22;
export const DEFAULT_PASSAGE_FONT_PCT = 100;

/** Type metrics for one scale stop. Both platforms render from exactly this. */
export type PassageFontMetrics = {
  fontSize: number;
  lineHeight: number;
};

/**
 * Clamps to the range and snaps to the step grid, so a stored value is always a
 * value the slider can actually point at. Shared by the server patch schema and
 * both clients; a non-finite input falls back to the default rather than
 * poisoning a layout with NaN.
 */
export function clampPassageFontPct(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_PASSAGE_FONT_PCT;
  const clamped = Math.min(PASSAGE_FONT_PCT_MAX, Math.max(PASSAGE_FONT_PCT_MIN, value));
  const steps = Math.round((clamped - PASSAGE_FONT_PCT_MIN) / PASSAGE_FONT_PCT_STEP);
  return PASSAGE_FONT_PCT_MIN + steps * PASSAGE_FONT_PCT_STEP;
}

/**
 * The single source of truth for passage type. At 100% this returns 22 / 44,
 * exactly what the old MD member rendered, so no existing learner's passage
 * changes size when the enum is migrated away.
 *
 * Leading grew WITH size under the old enum (1.85 at 18px, 2.0 at 22px, 2.15 at
 * 26px). That slope is kept, because it is what the reading page was tuned
 * against, but it is clamped at both ends: extrapolated raw it would reach 2.25
 * at the top of the new range (absurdly airy) and 1.46 at the bottom (lines
 * touching).
 */
export function passageFontMetrics(pct: number): PassageFontMetrics {
  const fontSize = round2((PASSAGE_FONT_BASE_PX * clampPassageFontPct(pct)) / 100);
  const ratio = Math.min(2.2, Math.max(1.7, 1.85 + (fontSize - 18) * 0.0375));
  return { fontSize, lineHeight: round2(fontSize * ratio) };
}

/**
 * Lines of sample the preview box reserves, at the DEFAULT scale.
 *
 * The box is a CONSTANT height on purpose. It used to size itself to its text,
 * so dragging the slider changed the sample's height and shoved the slider
 * itself up and down under the learner's finger - the control moved away from
 * the thing moving it. iOS solves the same problem the same way in Settings >
 * Accessibility > Larger Text: a fixed preview region that clips, with the
 * slider pinned below it.
 *
 * Four lines, measured rather than reasoned. Three was a guess and it clipped the
 * sample at the DEFAULT scale on a phone - it ended "mark..." - because ~80
 * characters wrap to four lines at 22pt in a settings row. The smallest stops
 * leave room to spare and the largest still clip after a line or two. That is the right way round: the preview exists to judge
 * GLYPH SIZE, not to read the whole passage, and a box tall enough never to clip
 * at 130% would eat most of a phone screen.
 */
export const PASSAGE_SAMPLE_LINES = 4;

/**
 * The sample box's height in px. Shared so web and iOS reserve the SAME space -
 * two platforms guessing separately is how the old px table drifted.
 */
export function passageSampleBoxHeight(): number {
  return round2(PASSAGE_SAMPLE_LINES * passageFontMetrics(DEFAULT_PASSAGE_FONT_PCT).lineHeight);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
